import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { formatTimestamp } from "../lib/formatTime.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import {
  INTEGRATION_KINDS,
  type TenantIntegrationKind
} from "../lib/integrationKinds.js";
import { IntegrationTestModal } from "../components/integrations/IntegrationTestModal.js";
import {
  MicrosoftIntegrationBody,
  type MicrosoftDetailBlock
} from "../components/integrations/MicrosoftIntegrationBody.js";
import { AiProviderIntegrationBody } from "../components/integrations/AiProviderIntegrationBody.js";
import type { AiProviderConfigShape } from "../lib/aiProvider.js";

/**
 * Tenant admin — Edit integration (ADR-0085 Phase 2 §5.4).
 *
 * Single Routing card with explicit Save — URL + User ID + Key live together.
 * No autosave. Below the Key input, a muted line surfaces the credentials
 * audit trail: "Credentials last updated by X on <timestamp>". Test connection opens
 * the inline test modal. Delete is gated on `used_by_count`.
 */

interface IntegrationDetailResponse {
  integration: {
    id: string;
    name: string;
    kind: TenantIntegrationKind;
    config: Record<string, unknown>;
    last_test_at: string | null;
    last_test_status: "success" | "failed" | null;
    last_test_error: string | null;
    created_at: string;
    updated_at: string;
    updated_by_email: string | null;
    used_by_count: number;
    credentials_updated_at: string | null;
    credentials_updated_by_email: string | null;
    config_decrypted_view: Record<string, unknown>;
    /** ADR-0155: present on microsoft_graph rows only. */
    microsoft?: MicrosoftDetailBlock;
  };
}

export function IntegrationDetail(): JSX.Element {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();

  const id = params.id ?? "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<IntegrationDetailResponse["integration"] | null>(null);

  // Form state — URL lives on config; user_id + key live on credentials.
  // The Key field is always blank on load (server never returns it).
  const [url, setUrl] = useState("");
  const [userId, setUserId] = useState("");
  const [key, setKey] = useState("");
  /** Snapshot of saved (url, userId) — Key is excluded since blank-means-preserve. */
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<IntegrationDetailResponse>(`/api/tenant/admin/integrations/${id}`);
      setData(res.integration);
      const initialUrl = (res.integration.config as { url?: string }).url ?? "";
      const initialUserId = (res.integration.config_decrypted_view as { user_id?: string })?.user_id ?? "";
      setUrl(initialUrl);
      setUserId(initialUserId);
      setKey("");
      setSavedSnapshot(JSON.stringify({ url: initialUrl, userId: initialUserId }));
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // ADR-0155: the admin-consent callback redirects here with ?consent=…
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const consent = searchParams.get("consent");
    if (!consent) return;
    if (consent === "granted") toast.push({ kind: "success", message: "Admin consent completed and verified." });
    else if (consent === "denied") toast.push({ kind: "error", message: "Consent was not granted. You can send a fresh package any time." });
    else toast.push({ kind: "error", message: "Consent could not be verified — see the connection card for details." });
    searchParams.delete("consent");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => {
    const current = JSON.stringify({ url, userId });
    const credsDirty = key.trim() !== "";
    return current !== savedSnapshot || credsDirty;
  }, [url, userId, key, savedSnapshot]);

  async function handleSave() {
    if (!data || !dirty) return;
    setSaving(true);
    try {
      const credsChanged = key.trim() !== "" || userId !== (data.config_decrypted_view as { user_id?: string })?.user_id;
      const body: { config?: Record<string, unknown>; credentials?: Record<string, unknown> } = {};

      // Only patch config when URL changed.
      const savedUrl = (data.config as { url?: string }).url ?? "";
      if (url !== savedUrl) {
        body.config = { url };
      }
      // Patch credentials when User ID changed OR Key non-blank. Blank Key
      // means "preserve existing key" per the route's documented semantics.
      if (credsChanged) {
        body.credentials = { user_id: userId, ...(key.trim() !== "" ? { key } : {}) };
      }
      if (!body.config && !body.credentials) {
        // Snapshot mismatch but nothing actually changed — defensive no-op.
        setSaving(false);
        return;
      }

      await apiFetch(`/api/tenant/admin/integrations/${id}`, {
        method: "PATCH",
        body
      });
      toast.push({ kind: "success", message: "Integration saved." });
      // Clear the Key field after save (never re-displayed) + reload so the
      // audit meta line picks up the new credentials_updated_at.
      setKey("");
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!data) return;
    const ok = await confirm(
      data.kind === "ai_provider"
        ? {
            // ADR-0207 §3.2, pre-cutover wording.
            title: "Remove this AI provider?",
            detail:
              "Atlas's AI features in this workspace will fall back to Atlas's shared AI until a provider is connected again, or until Atlas turns its shared AI off.",
            confirmLabel: "Remove"
          }
        : {
            title: `Delete "${data.name}"?`,
            detail: "Anyone using this integration will fail until it's recreated.",
            confirmLabel: "Delete"
          }
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/tenant/admin/integrations/${id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Integration deleted." });
      navigate("/app/admin/integrations");
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <PageLayout title="Integration"><LoadingState /></PageLayout>;
  if (error) return <PageLayout title="Integration"><ErrorState detail={error} /></PageLayout>;
  if (!data) return <PageLayout title="Integration"><ErrorState detail="Integration not found." /></PageLayout>;

  const meta = INTEGRATION_KINDS[data.kind];
  if (!meta) {
    return (
      <PageLayout title={data.name}>
        <ErrorState detail={`Unknown integration kind: ${data.kind}`} />
      </PageLayout>
    );
  }

  // Absolute timestamp inline (standing rule) — the line reads as a sentence,
  // so no tooltip is needed to recover the real instant.
  const credsAuditLine = (() => {
    if (!data.credentials_updated_at) return null;
    const when = formatTimestamp(data.credentials_updated_at);
    if (data.credentials_updated_by_email) {
      return `Credentials last updated by ${data.credentials_updated_by_email} on ${when}`;
    }
    return `Credentials last updated ${when}`;
  })();

  // ADR-0155: the Microsoft 365 body is its own surface — consent + site
  // access, no credential fields, its own Test gesture.
  if (data.kind === "microsoft_graph" && data.microsoft) {
    return (
      <PageLayout title={<><Link to="/app/admin/integrations">Integrations</Link> / Microsoft 365</> as unknown as string}>
        <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
          <MicrosoftIntegrationBody
            integrationId={id}
            microsoft={data.microsoft}
            lastTest={{ at: data.last_test_at, status: data.last_test_status, error: data.last_test_error }}
            onReload={() => void load()}
          />
          <div className="card">
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              Disconnecting is refused while sync policies depend on this connection. Copies already delivered stay
              where they are either way.
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn btnDanger"
                onClick={() => void handleDelete()}
                disabled={deleting || data.microsoft.dependent_policy_count > 0}
                title={data.microsoft.dependent_policy_count > 0 ? "Remove the dependent sync policies first" : undefined}
              >
                {deleting ? "Disconnecting…" : "Disconnect Microsoft 365"}
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // ADR-0207 §3.2: the AI provider — key, Foundry endpoint and deployments,
  // web search, Test, the decision package. Everything saves itself.
  if (data.kind === "ai_provider") {
    return (
      <PageLayout title={<><Link to="/app/admin/integrations">Integrations</Link> / AI provider</> as unknown as string}>
        <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
          <AiProviderIntegrationBody
            integrationId={id}
            config={data.config as unknown as AiProviderConfigShape}
            credsAuditLine={credsAuditLine}
            onReload={() => void load()}
            onTest={() => setShowTestModal(true)}
          />
          <div className="card">
            <div className="actions">
              <button type="button" className="btn btnDanger" onClick={() => void handleDelete()} disabled={deleting}>
                {deleting ? "Removing…" : "Remove AI provider"}
              </button>
            </div>
          </div>
        </div>
        {showTestModal ? (
          <IntegrationTestModal
            integrationId={id}
            integrationName="AI provider"
            kind={data.kind}
            onClose={() => {
              setShowTestModal(false);
              void load();
            }}
          />
        ) : null}
      </PageLayout>
    );
  }

  return (
    <PageLayout title={<><Link to="/app/admin/integrations">Integrations</Link> / {data.name}</> as unknown as string}>
      <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span className="pill">{meta.label}</span>{" "}
              <span className="muted">used by {data.used_by_count}</span>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => setShowTestModal(true)}
            >
              Test connection
            </button>
          </div>

          <PlexFields
            url={url}
            userId={userId}
            keyValue={key}
            onUrlChange={setUrl}
            onUserIdChange={setUserId}
            onKeyChange={setKey}
            credsAuditLine={credsAuditLine}
          />

          <div className="actions">
            <button
              type="button"
              className="btn btnPrimary"
              onClick={() => void handleSave()}
              disabled={saving || !dirty}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="actions">
            <button
              type="button"
              className="btn btnDanger"
              onClick={() => void handleDelete()}
              disabled={deleting || data.used_by_count > 0}
              title={data.used_by_count > 0 ? "In use — remove dependents first" : undefined}
            >
              {deleting ? "Deleting…" : "Delete integration"}
            </button>
          </div>
        </div>
      </div>

      {showTestModal ? (
        <IntegrationTestModal
          integrationId={id}
          integrationName={data.name}
          kind={data.kind}
          onClose={() => {
            setShowTestModal(false);
            void load();
          }}
        />
      ) : null}
    </PageLayout>
  );
}

function PlexFields(props: {
  url: string;
  userId: string;
  keyValue: string;
  onUrlChange: (v: string) => void;
  onUserIdChange: (v: string) => void;
  onKeyChange: (v: string) => void;
  credsAuditLine: string | null;
}): JSX.Element {
  const [showKey, setShowKey] = useState(false);
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label className="field">
        <span className="label">URL</span>
        <input
          className="input"
          type="text"
          value={props.url}
          onChange={(e) => props.onUrlChange(e.target.value)}
          placeholder="https://cloud.plex.com"
        />
        <div className="help">
          Your Plex API host root — e.g. <code>https://cloud.plex.com</code> (multi-tenant cloud) or
          <code> https://&lt;your-PCN&gt;.on.plex.com</code> (tenant-specific). Scheme + host only.
        </div>
      </label>

      <label className="field">
        <span className="label">User ID</span>
        <input
          className="input"
          type="text"
          value={props.userId}
          onChange={(e) => props.onUserIdChange(e.target.value)}
        />
        <div className="help">
          The Plex API username. Service account from Plex Customer Care, not your interactive login.
        </div>
      </label>

      <label className="field">
        <span className="label">Key</span>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="input"
            type={showKey ? "text" : "password"}
            value={props.keyValue}
            onChange={(e) => props.onKeyChange(e.target.value)}
            placeholder="Leave blank to keep existing key"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn"
            onClick={() => setShowKey((v) => !v)}
            aria-label={showKey ? "Hide key" : "Show key"}
          >
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        <div className="help">
          Stored encrypted; never displayed after save. Leave blank to keep the current key.
          {props.credsAuditLine ? (
            <>
              <br />
              <span className="muted">{props.credsAuditLine}</span>
            </>
          ) : null}
        </div>
      </label>
    </div>
  );
}
